import { ApiError, kioskStaffApi } from "@bw/shared/api";
import type {
  KioskCheckinFeed,
  KioskCheckinItem,
  KioskDevice,
} from "@bw/shared/types/generated";
import { useCallback, useEffect, useRef, useState } from "react";

/** New scans show up within this long. */
export const KIOSK_POLL_MS = 4_000;
/**
 * Every this many polls the whole window is read again, so an undo or resolve
 * by another staff member reaches this desk too (the cursor only brings new
 * scans).
 */
export const KIOSK_FULL_REFRESH_EVERY = 15;
const FEED_LIMIT = 50;
const KEEP = 100;

export interface KioskFeedState {
  items: KioskCheckinItem[];
  needsAPerson: KioskCheckinItem[];
  devices: KioskDevice[];
  loading: boolean;
  /** The last read failed; earlier data stays on screen. */
  error: string | null;
  /** 403: this person cannot see kiosk check-ins here. Polling stops. */
  forbidden: boolean;
  /** Apply a successful resolve or undo before the next poll confirms it. */
  patch: (scanId: string, change: Partial<KioskCheckinItem>) => void;
  refresh: () => void;
}

function mergeNewest(
  current: KioskCheckinItem[],
  incoming: KioskCheckinItem[],
): KioskCheckinItem[] {
  if (incoming.length === 0) return current;
  const byId = new Map(current.map((item) => [item.scan_id, item]));
  for (const item of incoming) byId.set(item.scan_id, item);
  return [...byId.values()]
    .sort((a, b) => b.scanned_at.localeCompare(a.scanned_at))
    .slice(0, KEEP);
}

const FEED_ERROR = "Kiosk check-ins are unavailable. Retrying.";

/**
 * The front desk's live kiosk list for one facility: first the last 12 hours,
 * then new scans by cursor every few seconds while the page is visible.
 * Mount it keyed by facility so a facility switch starts clean.
 */
export function useKioskFeed(facilityId: string): KioskFeedState {
  const [items, setItems] = useState<KioskCheckinItem[]>([]);
  const [needsAPerson, setNeeds] = useState<KioskCheckinItem[]>([]);
  const [devices, setDevices] = useState<KioskDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const cursor = useRef<string | null>(null);
  const polls = useRef(0);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const forbiddenRef = useRef(false);
  /** Local resolves/undos that a poll already in flight could still overwrite. */
  const patches = useRef(new Map<string, Partial<KioskCheckinItem>>());

  const withPatches = useCallback(
    (rows: KioskCheckinItem[]) =>
      rows.map((row) => {
        const change = patches.current.get(row.scan_id);
        return change ? { ...row, ...change } : row;
      }),
    [],
  );

  const read = useCallback(
    async (full: boolean) => {
      if (inFlight.current || forbiddenRef.current) return;
      inFlight.current = true;
      try {
        const since = full ? undefined : (cursor.current ?? undefined);
        const feed: KioskCheckinFeed = await kioskStaffApi.checkins(
          facilityId,
          {
            since,
            limit: FEED_LIMIT,
          },
        );
        if (!alive.current) return;
        cursor.current = feed.cursor;
        const rows = withPatches(feed.items);
        setItems((current) => (full ? rows : mergeNewest(current, rows)));
        setNeeds(
          withPatches(feed.needs_a_person).filter(
            (row) => !row.resolved_at && !row.undone_at,
          ),
        );
        setDevices(feed.devices);
        setError(null);
      } catch (cause) {
        if (!alive.current) return;
        if (cause instanceof ApiError && cause.status === 403) {
          forbiddenRef.current = true;
          setForbidden(true);
        } else {
          setError(FEED_ERROR);
        }
      } finally {
        inFlight.current = false;
        if (alive.current) setLoading(false);
      }
    },
    [facilityId, withPatches],
  );

  useEffect(() => {
    alive.current = true;
    void read(true);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      polls.current += 1;
      void read(polls.current % KIOSK_FULL_REFRESH_EVERY === 0);
    }, KIOSK_POLL_MS);
    return () => {
      alive.current = false;
      window.clearInterval(timer);
    };
  }, [read]);

  const patch = useCallback(
    (scanId: string, change: Partial<KioskCheckinItem>) => {
      patches.current.set(scanId, {
        ...patches.current.get(scanId),
        ...change,
      });
      const apply = (row: KioskCheckinItem) =>
        row.scan_id === scanId ? { ...row, ...change } : row;
      setItems((current) => current.map(apply));
      setNeeds((current) =>
        current.map(apply).filter((row) => !row.resolved_at && !row.undone_at),
      );
    },
    [],
  );

  const refresh = useCallback(() => {
    void read(true);
  }, [read]);

  return {
    items,
    needsAPerson,
    devices,
    loading,
    error,
    forbidden,
    patch,
    refresh,
  };
}
